/* ==== Class page is cached on cdn for 6 hours ==== */
import { SWRConfig } from 'swr';
import { useRouter } from 'next/router';
import Head from 'next/head';
import SEO from '@seo/index';
import classSchema from '@seo/class/class';
import LearnerClassLanding from '@cont/LearnerClass/Landing';
import Layout from '@layout/index';
import { isGTPGoal } from '@api/selectors/constant';
import {
  fetchClassDetails,
  fetchSimilarAndOtherCourses,
  fetchSimilarTopicPractice
} from '@api/fetchers/class';
import { fetchCourseData } from '@api/fetchers/freelesson';
import useCourseInfo, {
  getKey as getCourseInfoKey
} from '@api/hooks/course/useCourseInfo';
import useClassInfo, {
  getKey as getClassInfoKey
} from '@api/hooks/class/useClassInfo';
import { getKey as getSimilarAndOtherCoursesKey } from '@api/hooks/class/useSimilarAndOtherCourses';
import { getKey as getSimilarTopicPracticeKey } from '@api/hooks/class/useSimilarTopicPractice';
import { processCourseData } from '@api/hooks/course/utils';
import {
  processSimilarAndOtherCourses,
  processClassData
} from '@api/hooks/class/utils';
import { UnAuthorizedError } from '@utils/APICustomErrors';
import OneLinkSmartScript from '@Analytics/OneLinkSmartScript';
import { fetchGoalInfoLite } from '@api/fetchers/goal';
import { sessionStates } from '@constants/index';
import RecordedVideoBlockDialog from '@cont/Syllabus/TopicPage/chapterInfo/RecordedVideoBlockDialog';
import useConstants from '@api/hooks/useConstants';
import { getIsRCPlayAllowed } from '@api/selectors/constants';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  getMeCenterSubscription,
  getPlusSubscriptionsUIDS
} from '@api/selectors/subscription';
import { getActiveOfflineSubscriptionUIDs } from '@utils/subscriptionHelpers/getOfflineSubscriptionsAndUIDs';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getMe, getIsLoggedIn } from '@api/selectors/auth';
import cookies, { readCookie } from '@utils/cookies';
import { getClassDetailsEndpoint } from '@cont/Syllabus/constants/apiHelper';
import useFetch from '@hooks/useFetch';
import { fetchEducatorLevelDisplayConfig } from '@api/fetchers/profile';
import { getLevelDisplayInfo } from '@api/hooks/profile/useLevelDisplayInfo';
import validateAndSetSessionId, {
  DEVICE_ID_COOKIE_NAME
} from '@utils/session/validateAndSetSessionId';
import { fetchABExperimentData } from '@api/fetchers/experiments';
import { abProxyJWT, abVariations } from '@utils/abHelper';
import useExperiments from '@stores/experiments';
import { getExperimentVariation } from '@stores/selectors/experiments';
import ClassExperimentContext from '@cont/LearnerClass/ClassExpContext';
import { CLASS_EXPERIMENT_VARIATION_LOADED } from '@cont/LearnerClass/constants';
import { segment } from '@actions/analytics';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { localStorageAvailable } from '@utils/localStorage';
import { HOST_TYPE } from '@utils/api-helper/host';

const LearnerClassPageWithSEO = () => {
  const router = useRouter();

  const {
    query: { uid: classUID }
  } = router;

  const { classInfo } = useClassInfo(classUID);
  const { course: courseInfo } = useCourseInfo(classInfo.programme?.uid);

  const {
    uid: courseUID,
    goal = {},
    name,
    topic,
    language,
    authors,
    permalink,
    isPublic
  } = courseInfo;

  const { slug, uid } = classInfo;

  const canonicalURL =
    slug && uid ? `https://unacademy.com/class/${slug}/${uid}` : null;

  const metaTitle = !courseUID
    ? 'Class by Unacademy'
    : `${goal.name || 'Goal'} - ${
        name || 'Course'
      } Concepts Explained on Unacademy`;
  const metaDescription = !courseUID
    ? 'Watch Class by Unacademy'
    : `Understand the concept of ${name || 'course'} with ${
        goal.name || 'goal'
      } course curated by
  ${authors?.[0]?.name || 'educators'} on Unacademy. The ${
        topic || 'topic'
      } course is delivered in ${language}.`;

  const schema =
    courseUID && isPublic
      ? classSchema({
          courseDetails: courseInfo,
          classInfo: { name, permalink }
        })
      : null;

  const experimentVariation = useExperiments(
    getExperimentVariation('special_class_details_page_exp_v2')
  );
  const [expValue, setExpValue] = useState({
    expName: 'special_class_details_page_exp_v2',
    expVar: experimentVariation || abVariations.CONTROL,
    isFetched: false
  });
  const deviceID = cookies.readCookie(DEVICE_ID_COOKIE_NAME);
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const accessToken = readCookie('accessToken');
  const isLogInPopUpShown = useRef(false);
  useEffect(() => {
    if (localStorageAvailable()) {
      const hasNudgeBeenShown = localStorage.getItem('hasNudgeBeenShown');
      if (!isLogInPopUpShown.current && !accessToken) {
        fetchABExperimentData(
          'special_class_page_signup_nudge_v1',
          abProxyJWT,
          deviceID
        ).then((data) => {
          if (data.variation === abVariations.TEST_1 && !hasNudgeBeenShown) {
            openLoginDrawer();
            isLogInPopUpShown.current = true;
            localStorage.setItem('hasNudgeBeenShown', 'true');
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendEvent = (variation) => {
    segment.track(CLASS_EXPERIMENT_VARIATION_LOADED, {
      variation_name: variation
    });
  };
  const getExpData = async () => {
    validateAndSetSessionId();
    const res = await fetchABExperimentData(
      'special_class_details_page_exp_v2',
      abProxyJWT,
      deviceID
    );

    if (res.variation) {
      sendEvent(res.variation);
      setExpValue({
        ...expValue,
        expVar: res.variation,
        isFetched: true
      });
    } else
      setExpValue({
        ...expValue,
        isFetched: true
      });
  };

  useEffect(() => {
    getExpData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ClassExperimentContext.Provider value={expValue}>
      <SEO
        title={metaTitle}
        description={metaDescription}
        url={canonicalURL}
        openGraph={{
          title: metaTitle,
          description: metaDescription,
          url: canonicalURL,
          images: [
            {
              url: courseInfo.thumbnail || courseInfo.coverPhoto
            }
          ]
        }}
        schema={schema}
      />
      <Head>
        <title>{metaTitle}</title>
      </Head>
      <LearnerClassLanding />
      <OneLinkSmartScript />
    </ClassExperimentContext.Provider>
  );
};
const getRedirectionURL = (classData, defaultURL) => {
  const {
    programme: { batchGroup }
  } = classData;

  // redirecting the user to platform batch group detail page instead of course page in case of batch group lesson
  if (batchGroup?.uid) {
    return `/batch-group/${batchGroup.slug}/${batchGroup.uid}`;
  }

  return defaultURL;
};
const LearnerClassWithSWRConfig = ({
  classUID,
  courseUID,
  topicUID,
  classFallbackData,
  courseFallbackData,
  similarOtherCoursesFallbackData,
  similarTopicPracticeFallbackData,
  showRecordedGoalBlocker = false,
  goalUid,
  levelsDisplayInfo
}) => {
  const classInfoKey = getClassInfoKey(classUID);
  const courseInfoKey = getCourseInfoKey(courseUID);
  const similarAndOtherCoursesKey = getSimilarAndOtherCoursesKey(classUID);
  const similarTopicPracticeKey = getSimilarTopicPracticeKey(topicUID);
  const levelDisplayInfoKey = getLevelDisplayInfo();
  const { isValidating } = useConstants();
  const isRecordedContentPlayAllowed = useConstants(
    getIsRCPlayAllowed(goalUid)
  );
  const router = useRouter();
  const constants = useConstants();
  const meInfo = useMyInfo(getMe);
  const { goal = {}, forPlus, forSubscription } = courseFallbackData;
  const isPlusUser = useMemo(
    () => getPlusSubscriptionsUIDS(meInfo).includes(goal?.uid?.toUpperCase()),
    [meInfo, goal]
  );
  const classDetailEndpoint = getClassDetailsEndpoint(classUID);
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { data: classData, isLoading } = useFetch(
    isLoggedIn ? classDetailEndpoint : null,
    {
      hostType: HOST_TYPE.BACKEND_API_HOST
    }
  );

  useEffect(() => {
    try {
      const accToken = readCookie('accessToken');
      if (!accToken || (isLoggedIn && !isLoading && classData)) {
        const classDetailsData = classData || classFallbackData;
        const {
          query: { isSyllabusFeed }
        } = router;
        if (!classDetailsData?.uid) {
          router.push('/404');
          return;
        }
        const isSpecial =
          courseFallbackData?.isSpecial ||
          courseFallbackData?.programme?.isSpecial ||
          false;
        const isGTP = isGTPGoal(constants, goal?.uid);

        const isOfflineSubscription = getActiveOfflineSubscriptionUIDs(
          meInfo
        ).includes(goal?.uid?.toUpperCase());
        const hasCentreSubscription = getMeCenterSubscription(meInfo);

        // redirecting to gtp goal page if the user is not subscribed to GTP goal
        if (!(isPlusUser || isOfflineSubscription) && isGTP) {
          router.push(`/goal/-/${goal?.uid}`);
        } else {
          const defaultURL = `/course/${courseFallbackData?.slug}/${courseUID}/`;
          const {
            programme: { batchGroup },
            secondsBeforeLive,
            state,
            videoUrl
          } = classDetailsData;

          // redirecting to platform batch group detail page in case of course visibility date is in future for the platform batch group course
          if (
            (!isSpecial && batchGroup && secondsBeforeLive > 900) ||
            ((forPlus || forSubscription) &&
              !isSpecial &&
              secondsBeforeLive > 900 &&
              !isGTP) ||
            state === sessionStates.DORMANT ||
            (state === sessionStates.PAST &&
              !videoUrl &&
              !isSpecial &&
              !isGTP &&
              !(isSyllabusFeed === 'true')) ||
            hasCentreSubscription
          ) {
            router.push(getRedirectionURL(classDetailsData, defaultURL));
          }
        }
      }
    } catch (error) {
      router.push('/404');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isPlusUser,
    forPlus,
    forSubscription,
    classData,
    courseFallbackData,
    isLoggedIn
  ]);

  return (
    <SWRConfig
      value={{
        revalidateOnMount: false,
        dedupingInterval: 60 * 1000,
        fallback: {
          [courseInfoKey]: courseFallbackData,
          [classInfoKey]: classFallbackData,
          [similarAndOtherCoursesKey]: similarOtherCoursesFallbackData,
          [similarTopicPracticeKey]: similarTopicPracticeFallbackData,
          [levelDisplayInfoKey]: levelsDisplayInfo
        }
      }}
    >
      {showRecordedGoalBlocker &&
      !isValidating &&
      !isRecordedContentPlayAllowed ? (
        <RecordedVideoBlockDialog isOpen={showRecordedGoalBlocker} />
      ) : (
        <LearnerClassPageWithSEO />
      )}
    </SWRConfig>
  );
};

LearnerClassWithSWRConfig.pageInfo = {
  name: 'Free Live Class',
  layoutOptions: {
    hideIntercom: true,
    hideFreshbot: true,
    hidePlayer: true
  },
  isMWebSupported: true
};

export default LearnerClassWithSWRConfig;

export const getStaticProps = async ({ params }) => {
  const { uid: classUID } = params;
  const classData = await fetchClassDetails(classUID);
  try {
    let courseData;
    let goalInfo;
    let levelsDisplayInfo;
    if (classData?.programme?.goal?.uid) {
      [courseData, goalInfo, levelsDisplayInfo] = await Promise.all([
        fetchCourseData(classData.programme.uid),
        fetchGoalInfoLite(classData.programme.goal.uid, 'is_recorded_goal'),
        fetchEducatorLevelDisplayConfig()
      ]);
    } else {
      courseData = await fetchCourseData(classData.programme.uid);
      goalInfo = await fetchGoalInfoLite(
        classData.programme.goal.uid,
        'is_recorded_goal'
      );
    }
    const processedCourseData = processCourseData(courseData);
    const { isSpecial = false } = processedCourseData;
    const { isRecordedGoal } = goalInfo || {};
    const isLive =
      classData.type === 'post' && classData.state === sessionStates.LIVE;

    const props = {
      classUID,
      courseUID: classData.programme.uid,
      classFallbackData: processClassData(classData),
      courseFallbackData: processedCourseData,
      showRecordedGoalBlocker: !!(isRecordedGoal && !isLive && !isSpecial),
      goalUid: courseData.goalUid,
      levelsDisplayInfo
    };
    if (isSpecial) {
      const promises = [];
      promises.push(fetchSimilarAndOtherCourses(classUID));
      const topicGroup = courseData.topicGroups
        ? courseData.topicGroups[0]
        : null;
      if (topicGroup?.uid) {
        promises.push(fetchSimilarTopicPractice(topicGroup.uid));
        props.topicUID = topicGroup?.uid;
      }
      const [similarOtherCourses, similarTopicPractice] = await Promise.all(
        promises
      );
      props.similarOtherCoursesFallbackData =
        processSimilarAndOtherCourses(similarOtherCourses);
      if (similarTopicPractice)
        props.similarTopicPracticeFallbackData = similarTopicPractice;
    }
    return {
      props,
      revalidate: 5 // keeping the revalidate time to 5 seconds as this page is cached over cdn
    };
  } catch (err) {
    if (err instanceof UnAuthorizedError) {
      return {
        props: {
          classUID,
          courseUID: classData.programme.uid,
          classFallbackData: processClassData(classData),
          courseFallbackData: {
            processed: true
          },
          similarOtherCoursesFallbackData: {
            processed: true
          },
          showRecordedGoalBlocker: false
        },
        revalidate: 5 // 5 seconds
      };
    }

    return { notFound: true, revalidate: 5 };
  }
};

LearnerClassWithSWRConfig.Layout = Layout;

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};
